// Segmented control (CONTRACT "Controls"): inset track, 32 (sm 24), selected
// is tonal lift plus 600. Never blue. Arrow keys move the selection with a
// roving tabindex; Home and End jump. Options carry a label and an optional
// muted count.
import { useCallback, useRef, type KeyboardEvent } from 'react';
import { cx } from './cx';
import './Segmented.css';

export interface SegmentedOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly count?: number | string;
  readonly disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  readonly options: ReadonlyArray<SegmentedOption<T>>;
  readonly value: T;
  readonly onChange: (next: T) => void;
  readonly label: string;
  readonly size?: 'md' | 'sm';
  readonly full?: boolean;
  readonly className?: string;
}

function nextEnabledIndex<T extends string>(options: ReadonlyArray<SegmentedOption<T>>, from: number, step: 1 | -1): number {
  const n = options.length;
  let i = from;
  for (let tries = 0; tries < n; tries += 1) {
    i = (i + step + n) % n;
    if (!options[i]?.disabled) return i;
  }
  return from;
}

export function Segmented<T extends string>({ options, value, onChange, label, size = 'md', full = false, className }: SegmentedProps<T>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));

  const focusIndex = useCallback((index: number) => {
    const buttons = rootRef.current?.querySelectorAll<HTMLButtonElement>('button');
    buttons?.[index]?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let target: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = nextEnabledIndex(options, selectedIndex, 1);
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = nextEnabledIndex(options, selectedIndex, -1);
    else if (event.key === 'Home') target = options.findIndex((o) => !o.disabled);
    else if (event.key === 'End') target = options.length - 1 - [...options].reverse().findIndex((o) => !o.disabled);
    if (target === null || target < 0 || target === selectedIndex) return;
    event.preventDefault();
    const option = options[target];
    if (!option) return;
    onChange(option.value);
    focusIndex(target);
  };

  return (
    <div
      ref={rootRef}
      role="radiogroup"
      aria-label={label}
      className={cx('seg', size === 'sm' && 'seg-sm', full && 'seg-full', className)}
      onKeyDown={onKeyDown}
    >
      {options.map((option, index) => {
        const selected = index === selectedIndex;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={option.disabled}
            className="seg-btn"
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined ? <span className="seg-n num">{option.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
